import { useRef, useState, useCallback, useEffect } from 'react';

export const TRIGGER_HEIGHT = 160;
const SPINNER_DISPLAY_DURATION = 1000;

interface UsePullToRefreshProps {
	onRefresh: () => Promise<void>;
}

interface UsePullToRefreshReturn {
	pullHeight: number;
	isRefreshing: boolean;
	pullRatio: number;
	handleTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
	handleTouchMove: (e: React.TouchEvent<HTMLDivElement>) => Promise<void>;
	handleTouchEnd: () => void;
	lastUpdated: Date | null;
}

const usePullToRefresh = ({ onRefresh }: UsePullToRefreshProps): UsePullToRefreshReturn => {
	const startYRef = useRef<number | null>(null);
	const [pullHeight, setPullHeight] = useState(0);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

	useEffect(() => {
		let startY: number | null = null;
		const handleTouchStart = (e: TouchEvent) => {
			const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
			if (scrollTop <= 0) {
				startY = e.touches[0].clientY;
			}
		};

		const handleTouchMove = (e: TouchEvent) => {
			if (startY === null) return;
			const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
			const currentY = e.touches[0].clientY;
			const deltaY = currentY - startY;
			if (scrollTop <= 0 && deltaY > 0) {
				e.preventDefault();
			}
		};

		const handleTouchEnd = () => {
			startY = null;
		};

		document.addEventListener('touchstart', handleTouchStart, { passive: true });
		document.addEventListener('touchmove', handleTouchMove, { passive: false });
		document.addEventListener('touchend', handleTouchEnd, { passive: true });

		return () => {
			document.removeEventListener('touchstart', handleTouchStart);
			document.removeEventListener('touchmove', handleTouchMove);
			document.removeEventListener('touchend', handleTouchEnd);
		};
	}, []);

	const handleRefresh = useCallback(async () => {
		if (isRefreshing) return;
		setIsRefreshing(true);
		try {
			await onRefresh();
			setLastUpdated(new Date());
		} finally {
			setTimeout(() => {
				setPullHeight(0);
				setIsRefreshing(false);
				setLastUpdated(null);
			}, SPINNER_DISPLAY_DURATION);
		}
	}, [onRefresh, isRefreshing]);

	const calculatePullHeight = useCallback((deltaY: number): number => {
		return Math.min(deltaY, TRIGGER_HEIGHT);
	}, []);

	const shouldTriggerRefresh = useCallback(
		(height: number): boolean => {
			return height >= TRIGGER_HEIGHT && !isRefreshing;
		},
		[isRefreshing],
	);

	const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
		const scrollTop = e.currentTarget.scrollTop || document.documentElement.scrollTop || document.body.scrollTop;
		if (scrollTop <= 0) {
			startYRef.current = e.touches[0].clientY;
		}
	}, []);

	const handleTouchMove = useCallback(
		async (e: React.TouchEvent<HTMLDivElement>) => {
			if (startYRef.current === null) return;

			const currentY = e.touches[0].clientY;
			const deltaY = currentY - startYRef.current;
			const scrollTop = e.currentTarget.scrollTop || document.documentElement.scrollTop || document.body.scrollTop;
			
			//  Disable native scroll when at viewport top to prevent iOS bounce effect and enable custom PTR
			if (scrollTop <= 0 && deltaY > 0) {
				const newPullHeight = calculatePullHeight(deltaY);
				setPullHeight(newPullHeight);
				if (shouldTriggerRefresh(newPullHeight)) {
					await handleRefresh();
				}
			}
		},
		[calculatePullHeight, shouldTriggerRefresh, handleRefresh],
	);

	const handleTouchEnd = useCallback(() => {
		if (pullHeight < TRIGGER_HEIGHT) {
			setPullHeight(0);
		}
		startYRef.current = null;
	}, [pullHeight]);

	const pullRatio = Math.min(pullHeight / TRIGGER_HEIGHT, 1);

	return {
		pullHeight,
		isRefreshing,
		pullRatio,
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
		lastUpdated,
	};
};

export default usePullToRefresh;
